import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { PropsWithChildren, ReactNode, useState } from 'react';

import { ModalErrorBoundary } from '@flipster/features/src/utils/modal-error-boundary';
import { FormattedMessage } from '@flipster/i18n';
import { ordersBunja } from '@flipster/r230522/original/order';
import { openPositionsBunja } from '@flipster/r230522/original/position';
import { Badge } from '@flipster/react/design-system/badge';
import { Button } from '@flipster/react/design-system/button';
import { Modal } from '@flipster/react/design-system/modal/index';
import { RadioButton } from '@flipster/react/design-system/radio-button';
import { TextButton } from '@flipster/react/design-system/text-button';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack, vstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { numberFormatter } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';

import { symbolBunja, useMarginType } from '../bunja/base';
import { marginTypeToEventTypeMap } from '../bunja/event';
import { formBunja } from '../bunja/form';
import { DefaultLeverageMarginModeButton } from './default-leverage-margin-mode-button';
import { DefaultLeverageMarginModeContent } from './default-leverage-margin-mode-modal';
import { marginTypeSelectModalBunja } from './margin-type-select-modal-bunja';

type Step = 'main' | 'defaultLeverageMarginMode';

interface MarginTypeSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

const MarginTypeSelectModal = ({ isOpen, onClose, onExit }: MarginTypeSelectModalProps) => {
  const [step, setStep] = useState<Step>('main');
  useBunja(marginTypeSelectModalBunja); // to keep selected margin type regardless step

  return (
    <Modal.Root onClose={onClose} isOpen={isOpen} onExit={onExit}>
      <ModalErrorBoundary onError={onExit}>
        {step === 'main' && (
          <MarginTypeSelectContent
            onClose={onClose}
            onExit={onExit}
            onOpenDefaultLeverageMarginMode={() => setStep('defaultLeverageMarginMode')}
          />
        )}
        {step === 'defaultLeverageMarginMode' && (
          <DefaultLeverageMarginModeContent onBack={() => setStep('main')} afterConfirm={onClose} />
        )}
      </ModalErrorBoundary>
    </Modal.Root>
  );
};

export default MarginTypeSelectModal;

interface MarginTypeSelectContentProps {
  onClose: () => void;
  onExit: () => void;
  onOpenDefaultLeverageMarginMode: () => void;
}

const MarginTypeSelectContent = ({
  onClose,
  onExit,
  onOpenDefaultLeverageMarginMode,
}: MarginTypeSelectContentProps) => {
  const { symbol } = useBunja(symbolBunja);
  const { v2OrdersAtom } = useBunja(ordersBunja);
  const { openPositionsAtom } = useBunja(openPositionsBunja);
  const { updateMarginType } = useMarginType();
  const { handleLeverageChangeAtom } = useBunja(formBunja);
  const handleLeverageChange = useSetAtom(handleLeverageChangeAtom);

  const {
    selectedMarginTypeAtom,
    leverageAtom,
    crossMaxAtom,
    effectiveIsolatedMaxAtom,
    ceilingsDifferAtom,
    crossMarginSupportedAtom,
    showLeverageChangeNoticeAtom,
  } = useBunja(marginTypeSelectModalBunja);
  const [marginType, setMarginType] = useAtom(selectedMarginTypeAtom);
  const leverage = useAtomValue(leverageAtom);
  const crossMax = useAtomValue(crossMaxAtom);
  const effectiveIsolatedMax = useAtomValue(effectiveIsolatedMaxAtom);
  const showCeilingsBadges = useAtomValue(ceilingsDifferAtom);
  const crossMarginSupported = useAtomValue(crossMarginSupportedAtom);
  const showLeverageChangeNotice = useAtomValue(showLeverageChangeNoticeAtom);

  const orders = useAtomValue(v2OrdersAtom)[symbol] ?? [];
  const openPositions = useAtomValue(openPositionsAtom)[symbol] ?? [];
  const hasOtherOrders = orders.length > 0 || openPositions.some((position) => position.airdropType == null);

  return (
    <>
      <Modal.Header>
        <Modal.Title label={<FormattedMessage id='Trade.r251008.Margin_mode' />} />
      </Modal.Header>
      <Modal.Content>
        <fieldset className={vstack({ gap: 8 })}>
          <RadioContainer
            extra={
              showLeverageChangeNotice ? <LeverageChangeNotice from={leverage} to={effectiveIsolatedMax} /> : undefined
            }
          >
            <div className={hstack({ justify: 'space-between', gap: 8, w: '100%' })}>
              <RadioButton
                name='marginType'
                value='Isolated'
                disabled={marginType !== 'Isolated' && hasOtherOrders}
                checked={marginType === 'Isolated'}
                onChange={() => {
                  segment.track('order_form_margin_mode_click', {
                    symbol,
                    margin_mode: marginTypeToEventTypeMap.Isolated,
                  });
                  setMarginType('Isolated');
                }}
                css={{ color: 'content.default.level-5' }}
              >
                <FormattedMessage id='Trade.r250401.Isolated_margin' />
              </RadioButton>
              {showCeilingsBadges && <MaxLeverageBadge leverage={effectiveIsolatedMax} />}
            </div>
            <RadioDescription description={<FormattedMessage id='Trade.r251008.Margin_mode_desc_isolated_margin' />} />
          </RadioContainer>
          <RadioContainer
            extra={
              <div className={css({ bg: 'background.default.level-1', px: 20, py: 16 })}>
                <div className={stack({ gap: 6 })}>
                  <span className={css({ textStyle: 'Body/S/Regular', color: 'content.default.level-5' })}>
                    <FormattedMessage id='Trade.r251008.Cross_asset' />
                  </span>
                  <p className={css({ mt: 2, textStyle: 'Body/XS/Regular', color: 'content.default.level-2' })}>
                    <FormattedMessage id='Trade.r251008.Margin_mode_desc_cross_asset' />
                  </p>
                </div>
                <TextButton
                  as='a'
                  href='/collateral-overview'
                  css={{ mt: 8 }}
                  color='neutral'
                  size='small'
                  label={<FormattedMessage id='Common.r250401.Learn_more' />}
                  onClick={() => {
                    onExit();
                  }}
                />
              </div>
            }
          >
            <div className={hstack({ justify: 'space-between', gap: 8, w: '100%' })}>
              <RadioButton
                name='marginType'
                value='Cross'
                disabled={marginType !== 'Cross' && (!crossMarginSupported || hasOtherOrders)}
                checked={marginType === 'Cross'}
                onChange={() => {
                  segment.track('order_form_margin_mode_click', {
                    symbol,
                    margin_mode: marginTypeToEventTypeMap.Cross,
                  });
                  setMarginType('Cross');
                }}
                css={{ color: 'content.default.level-5' }}
              >
                <FormattedMessage id='Trade.r251008.Cross_margin' />
              </RadioButton>
              {showCeilingsBadges && <MaxLeverageBadge leverage={crossMax} />}
            </div>
            <RadioDescription description={<FormattedMessage id='Trade.r251008.Margin_mode_desc_cross_margin' />} />
          </RadioContainer>
        </fieldset>
        {crossMarginSupported && hasOtherOrders && (
          <div className={flex({ mt: 16, gap: 4, align: 'center', color: 'content.state.warning' })}>
            <InformationCircleIcon size={20} />
            <p className={css({ textStyle: 'Text/M/Regular' })}>
              <FormattedMessage id='Trade.r251008.Close_positions_pending_orders_info' />
            </p>
          </div>
        )}
        {!crossMarginSupported && (
          <div className={flex({ mt: 16, gap: 4, align: 'center' })}>
            <InformationCircleIcon size={20} color={token('colors.background.accent.primary')} />
            <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
              <FormattedMessage id='Trade.r251008.Support_isolated_margin_only_info' />
            </p>
          </div>
        )}
        <DefaultLeverageMarginModeButton onClick={onOpenDefaultLeverageMarginMode} />
      </Modal.Content>
      <Modal.Footer>
        <Button
          size='large'
          label={<FormattedMessage id='Common.r250401.Confirm' />}
          disabled={hasOtherOrders}
          onClick={() => {
            segment.track('order_form_margin_mode_confirm_click', {
              symbol,
              margin_mode: marginTypeToEventTypeMap[marginType],
            });
            updateMarginType(marginType);
            if (marginType === 'Isolated' && leverage > effectiveIsolatedMax) {
              handleLeverageChange(effectiveIsolatedMax);
            }
            onClose();
          }}
          css={{ w: '100%' }}
        />
      </Modal.Footer>
    </>
  );
};

const MaxLeverageBadge = ({ leverage }: { leverage: number }) => (
  <Badge color='neutral' css={{ flexShrink: 0 }}>
    <FormattedMessage id='Common.r250401.Max' /> {numberFormatter(leverage)}x
  </Badge>
);

const LeverageChangeNotice = ({ from, to }: { from: number; to: number }) => (
  <div
    className={css({
      w: '100%',
      py: 12,
      px: 20,
      bg: 'background.accent.primary.tint',
      textStyle: 'Body/XS/Regular',
      color: 'content.default.level-4',
    })}
  >
    <FormattedMessage id='Trade.r260806.Leverage_will_change' />{' '}
    <span className={css({ textStyle: 'Body/XS/Strong', color: 'content.accent.info' })}>
      {numberFormatter(from)}x → {numberFormatter(to)}x
    </span>
  </div>
);

const RadioContainer = ({ children, extra }: PropsWithChildren<{ extra?: ReactNode }>) => {
  return (
    <div
      className={flex({
        direction: 'column',
        gap: 0,
        borderRadius: 16,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'border.default.level-3',
        overflow: 'hidden',
      })}
    >
      <div className={stack({ px: 20, py: 16, gap: 6 })}>{children}</div>
      {extra}
    </div>
  );
};

interface RadioDescriptionProps {
  description: ReactNode;
  extra?: ReactNode;
}
const RadioDescription = ({ description, extra }: RadioDescriptionProps) => {
  return (
    <div className={flex({ direction: 'column', gap: 12 })}>
      <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>{description}</span>
      {extra && <div className={css({ bg: 'background.default.level-1' })}>{extra}</div>}
    </div>
  );
};
